import { useState, useRef, useEffect } from 'react';
import { Play, Volume2, VolumeX } from 'lucide-react';

const VideoCard = ({ videoUrl, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef(null);

  const getVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const getYouTubeEmbedUrl = (url, autoplay = false, muted = true) => {
    const videoId = getVideoId(url);
    if (!videoId) return url;
    
    // YouTube embed with autoplay parameters
    // mute=1 is required for autoplay to work in most browsers
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      mute: muted ? '1' : '0',
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
      enablejsapi: '1',
      controls: '1'
    });
    
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = getVideoId(url);
    if (!videoId) return 'https://images.unsplash.com/photo-1727411238880-2aaa8f2e276b?crop=entropy&cs=srgb&fm=jpg&q=85';
    // Use maxresdefault with fallback to hqdefault
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };
const handleIframeLoad = () => {
  if (iframeRef.current?.contentWindow) {
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'playVideo',
        args: []
      }),
      '*'
    );

    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'mute',
        args: []
      }),
      '*'
    );
  }
};
  const toggleMute = (e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  };

  // Update iframe src when mute state changes
  useEffect(() => {
    if (isPlaying && iframeRef.current) {
      const videoId = getVideoId(videoUrl);
      if (videoId) {
        // Post message to YouTube iframe API to toggle mute
        iframeRef.current.contentWindow?.postMessage(
          JSON.stringify({
            event: 'command',
            func: isMuted ? 'mute' : 'unMute'
          }),
          '*'
        );
      }
    }
  }, [isMuted, isPlaying, videoUrl]);

  if (!videoUrl) {
    return (
      <div
        className={`relative group w-full aspect-video bg-black rounded-sm overflow-hidden border border-white/10 shadow-2xl flex items-center justify-center ${className}`}
        data-testid="video-card-placeholder"
      >
        <div className="text-center p-8">
          <Play className="w-16 h-16 text-gold/50 mx-auto mb-4" />
          <p className="text-gray-500 font-manrope">Video will be added here</p>
        </div>
      </div>
    );
  }

  if (isPlaying) {
    return (
      <div className={`relative group w-full aspect-video bg-black rounded-sm overflow-hidden border border-gold shadow-2xl ring-1 ring-gold ${className}`} data-testid="video-card-playing">
        <div className="youtube-container">
          <iframe
            ref={iframeRef}
            src={getYouTubeEmbedUrl(videoUrl, true, true)}
            title="YouTube video"
            onLoad={handleIframeLoad}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {/* Unmute button overlay - shows briefly */}
        {isMuted && (
          <button
            onClick={toggleMute}
            className="absolute bottom-4 right-4 z-10 px-4 py-2 bg-black/80 hover:bg-gold text-white hover:text-black rounded-sm flex items-center gap-2 transition-all duration-300 font-manrope text-sm"
            data-testid="unmute-button"
          >
            <VolumeX className="w-4 h-4" />
            Click to unmute
          </button>
        )}
        {!isMuted && (
          <button
            onClick={toggleMute}
            className="absolute bottom-4 right-4 z-10 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-all opacity-0 group-hover:opacity-100"
            data-testid="mute-button"
          >
            <Volume2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative group w-full aspect-video bg-black rounded-sm overflow-hidden border border-white/10 shadow-2xl cursor-pointer hover:border-gold/50 transition-all duration-300 ${className}`}
      onClick={handlePlay}
      data-testid="video-card-thumbnail"
    >
      <img
        src={getYouTubeThumbnail(videoUrl)}
        alt="Video thumbnail"
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
        onError={(e) => {
          // Fallback to hqdefault if maxresdefault fails
          const videoId = getVideoId(videoUrl);
          if (videoId && !e.target.src.includes('hqdefault')) {
            e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
          }
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition-all">
        <div className="w-20 h-20 rounded-full bg-gold/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 gold-glow">
          <Play className="w-10 h-10 text-black ml-1" fill="currentColor" />
        </div>
      </div>
      {/* Click to play hint */}
      <div className="absolute bottom-4 left-4 text-white/70 text-sm font-manrope opacity-0 group-hover:opacity-100 transition-opacity">
        Click to play
      </div>
    </div>
  );
};

export default VideoCard;